'use client';

import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {Container, Typography, useMediaQuery, useTheme} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {PopupSec} from '@/app/[locale]/[[...slug]]/types';
import {PopupItemFragment} from '@generated/graphql';
import ContentfulImage from '@/components/ui/image/ContentfulImage';
import {RichTextRenderer} from '@/components/contentful/RichTextRenderer';
import {Document} from '@contentful/rich-text-types';
import {
  StyledPopupSectionContainer,
  StyledPopupRowWrapper,
  StyledPopupRow,
  StyledPopupCard,
  StyledPopupCardImageWrapper,
  StyledPopupCardImageContainer,
  StyledPopupBadge,
  StyledPopupBadgeIconContainer,
  StyledPopupBadgeIcon,
  StyledPopupExpandedRow,
  StyledPopupExpandedImageWrapper,
  StyledPopupExpandedImageContainer,
  StyledPopupExpandedContent,
  StyledPopupCloseButton,
} from '@/components/sections/popup-section/PopupSection.styles';

type PopupSectionProps = {
  section: PopupSec;
};

type ValidItem = PopupItemFragment & { image: { url: string } };

function isValidItem(item: PopupItemFragment | null): item is ValidItem {
  return item != null && !!item.image?.url;
}

function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

export const PopupSection: FC<PopupSectionProps> = ({section}) => {
  const heading = section.heading;
  const items = section.popupItemsCollection?.items ?? [];
  const validItems = items.filter(isValidItem);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (expandedId === null) return;

    lastFocusedRef.current = document.activeElement as HTMLElement | null;
    const focusTimeout = window.setTimeout(() => closeButtonRef.current?.focus(), 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpandedId(null);
    };
    document.addEventListener('keydown', onKey);

    return () => {
      window.clearTimeout(focusTimeout);
      document.removeEventListener('keydown', onKey);
      lastFocusedRef.current?.focus();
    };
  }, [expandedId]);

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));
  const columnsPerRow = isSmall ? 1 : isMedium ? 2 : 3;

  const rows = useMemo(() => chunkArray(validItems, columnsPerRow), [validItems, columnsPerRow]);

  return (
    <Container maxWidth='mdlg' {...section.id && { id: section.id }}>
      {heading && (
        <Typography variant='h2' sx={{color: 'text.dark', mb: 2, textAlign: 'center'}}>{heading}</Typography>
      )}

      <StyledPopupSectionContainer>
        {rows.map((row, rowIndex) => {
          const expandedItem = row.find((item) => item.sys.id === expandedId);

          return (
            <StyledPopupRowWrapper key={`row-${rowIndex}`}>
              {/* Grid always renders — defines the row height */}
              <StyledPopupRow columns={columnsPerRow} hasExpanded={!!expandedItem}>
                {row.map((item) => {
                  const isExpanded = item.sys.id === expandedId;
                  return (
                    <StyledPopupCard
                      key={item.sys.id}
                      type='button'
                      onClick={() => setExpandedId(item.sys.id)}
                      aria-label={item.heading ?? 'Open popup'}
                      aria-expanded={isExpanded}
                      aria-controls={`popup-expanded-${item.sys.id}`}
                    >
                      <StyledPopupCardImageWrapper>
                        <StyledPopupCardImageContainer>
                          <ContentfulImage
                            src={item.image.url}
                            alt={item.heading ?? ''}
                            fill
                            sizes='(max-width: 600px) 360px, 500px'
                            style={{objectFit: 'cover'}}
                            focalX={item.focalX}
                            focalY={item.focalY}
                            quality={85}
                          />
                        </StyledPopupCardImageContainer>

                        <StyledPopupBadge>
                          <StyledPopupBadgeIconContainer>
                            <StyledPopupBadgeIcon fontSize='small' />
                          </StyledPopupBadgeIconContainer>
                        </StyledPopupBadge>
                      </StyledPopupCardImageWrapper>

                      {item.heading && (
                        <Typography variant='h4' sx={{fontWeight: 600, color: 'text.dark', textAlign: 'center', mt: 1}}>
                          {item.heading}
                        </Typography>
                      )}
                    </StyledPopupCard>
                  );
                })}
              </StyledPopupRow>

              {/* Expanded overlay — absolute, covers the row */}
              {expandedItem && (
                <StyledPopupExpandedRow
                  id={`popup-expanded-${expandedItem.sys.id}`}
                  role='dialog'
                  aria-modal='true'
                  {...(expandedItem.heading
                    ? {'aria-labelledby': `popup-heading-${expandedItem.sys.id}`}
                    : {'aria-label': 'Expanded item'})}
                >
                  <StyledPopupCloseButton
                    ref={closeButtonRef}
                    type='button'
                    onClick={() => setExpandedId(null)}
                    aria-label='Close popup'
                  >
                    <CloseIcon fontSize='small' sx={{color: 'text.dark'}} />
                  </StyledPopupCloseButton>

                  <StyledPopupExpandedImageWrapper>
                    <StyledPopupExpandedImageContainer>
                      <ContentfulImage
                        src={expandedItem.image.url}
                        alt={expandedItem.heading ?? ''}
                        fill
                        sizes='(max-width: 600px) 180px, 250px'
                        style={{objectFit: 'cover'}}
                        focalX={expandedItem.focalX}
                        focalY={expandedItem.focalY}
                      />
                    </StyledPopupExpandedImageContainer>

                    {expandedItem.heading && (
                      <Typography
                        id={`popup-heading-${expandedItem.sys.id}`}
                        variant='h4'
                        sx={{fontWeight: 600, color: 'text.dark', mt: 1.5, textAlign: 'center'}}
                      >
                        {expandedItem.heading}
                      </Typography>
                    )}
                  </StyledPopupExpandedImageWrapper>

                  {expandedItem.content && (
                    <StyledPopupExpandedContent>
                      <RichTextRenderer
                        richTextDocument={expandedItem.content.json as unknown as Document}
                      />
                    </StyledPopupExpandedContent>
                  )}
                </StyledPopupExpandedRow>
              )}
            </StyledPopupRowWrapper>
          );
        })}
      </StyledPopupSectionContainer>
    </Container>
  );
};
